import { forwardRef } from "react";
import { cn } from "@/lib/utils";
import { Spin } from "./Spin";

export interface CardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
	title?: React.ReactNode;
	extra?: React.ReactNode;
	bordered?: boolean;
	hoverable?: boolean;
	loading?: boolean;
	size?: "default" | "small";
	bodyClassName?: string;
	styles?: {
		header?: React.CSSProperties;
		body?: React.CSSProperties;
	};
}

const Card = forwardRef<HTMLDivElement, CardProps>(
	(
		{
			className,
			title,
			extra,
			bordered = true,
			hoverable = false,
			loading = false,
			size = "default",
			bodyClassName,
			styles,
			children,
			...props
		},
		ref,
	) => {
		const hasHeader = title || extra;

		return (
			<div
				ref={ref}
				className={cn(
					"bg-white dark:bg-gray-800 rounded-lg",
					bordered && "border border-gray-200 dark:border-gray-700",
					hoverable &&
						"cursor-pointer transition-shadow hover:shadow-lg dark:hover:shadow-gray-900/30",
					!bordered && "shadow-sm",
					className,
				)}
				{...props}
			>
				{hasHeader && (
					<div
						className={cn(
							"flex items-center justify-between border-b border-gray-200 dark:border-gray-700",
							size === "small" ? "px-3 py-2" : "px-6 py-4",
						)}
						style={styles?.header}
					>
						{title && <div className="font-medium text-gray-900 dark:text-gray-100">{title}</div>}
						{extra && <div className="ml-auto">{extra}</div>}
					</div>
				)}
				<div className={cn(size === "small" ? "p-3" : "p-6", bodyClassName)} style={styles?.body}>
					{loading ? (
						<div className="flex items-center justify-center py-8">
							<Spin />
						</div>
					) : (
						children
					)}
				</div>
			</div>
		);
	},
);

Card.displayName = "Card";

export { Card };
